import { $isLinkNode, $toggleLink, LinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import {
  $convertFromMarkdownString,
  $convertToMarkdownString,
  TRANSFORMERS,
} from "@lexical/markdown";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import {
  HeadingNode,
  QuoteNode,
  $isHeadingNode,
  $isQuoteNode,
  $createHeadingNode,
  $createQuoteNode,
} from "@lexical/rich-text";
import { $setBlocksType } from "@lexical/selection";
import { $getNearestNodeOfType } from "@lexical/utils";
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  $createParagraphNode,
  ParagraphNode,
} from "lexical";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { LEXICAL_TEXT_THEME, LEXICAL_TEXT_THEME_ACCENT_LINKS } from "./lexicalTextTheme";
import { decodeBlankLines, encodeBlankLines } from "./markdownBlankLines";

const LexicalErrorBoundary: React.FC<{
  children: React.ReactElement;
  onError: (error: Error) => void;
}> = ({ children }) => <>{children}</>;

interface ToolbarButtonProps {
  icon?: string;
  label: string;
  onClick: () => void;
  active?: boolean;
  children?: React.ReactNode;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({
  icon,
  label,
  onClick,
  active,
  children,
}) => (
  <button
    onMouseDown={e => {
      e.preventDefault();
      onClick();
    }}
    title={label}
    className={`w-7 h-7 flex items-center justify-center rounded hover:bg-gray-100 cursor-pointer transition-colors text-gray-600 ${
      active ? "bg-gray-200 text-gray-900" : ""
    }`}
  >
    {children ?? <span className="material-icons !text-[18px]">{icon}</span>}
  </button>
);

const BLOCK_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "paragraph", label: "Normal" },
  { value: "h1", label: "Heading 1" },
  { value: "h2", label: "Heading 2" },
  { value: "h3", label: "Heading 3" },
  { value: "h4", label: "Heading 4" },
  { value: "h5", label: "Heading 5" },
  { value: "h6", label: "Heading 6" },
  { value: "quote", label: "Quote" },
];

const getBlockTypeLabel = (type: string): string =>
  BLOCK_TYPE_OPTIONS.find(o => o.value === type)?.label ?? "Normal";

// Standard Tailwind size classes only — arbitrary values like text-[15px] don't
// resolve in every document this editor renders into, which made the previews
// fall back to a larger inherited size (H3 showed bigger than H2). H4–H6 share
// the smallest size and step down by color instead.
const BLOCK_TYPE_STYLES: Record<string, string> = {
  paragraph: "text-sm",
  h1: "text-lg font-bold",
  h2: "text-base font-bold",
  h3: "text-sm font-semibold",
  h4: "text-xs font-semibold",
  h5: "text-xs font-semibold text-gray-600",
  h6: "text-xs font-semibold text-gray-400",
  quote: "text-sm italic text-gray-500",
};

const ToolbarPlugin: React.FC<{ maxHeadingLevel: number; showLinkButton: boolean }> = ({
  maxHeadingLevel,
  showLinkButton,
}) => {
  const [editor] = useLexicalComposerContext();
  const blockTypeOptions = BLOCK_TYPE_OPTIONS.filter(opt => {
    const level = opt.value.match(/^h(\d)$/);
    return !level || Number(level[1]) <= maxHeadingLevel;
  });
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isCode, setIsCode] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [blockType, setBlockType] = useState("paragraph");
  const [listType, setListType] = useState<"bullet" | "number" | null>(null);
  const [showBlockDropdown, setShowBlockDropdown] = useState(false);
  const [showLinkPopover, setShowLinkPopover] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [existingLinkUrl, setExistingLinkUrl] = useState("");
  const blockDropdownRef = useRef<HTMLDivElement>(null);
  const linkPopoverRef = useRef<HTMLDivElement>(null);
  const linkInputRef = useRef<HTMLInputElement>(null);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat("bold"));
      setIsItalic(selection.hasFormat("italic"));
      setIsStrikethrough(selection.hasFormat("strikethrough"));
      setIsCode(selection.hasFormat("code"));

      const anchorNode = selection.anchor.getNode();
      const parent = anchorNode.getParent();
      const linkNode = $isLinkNode(parent) ? parent : $isLinkNode(anchorNode) ? anchorNode : null;
      setIsLink(!!linkNode);
      if (linkNode && $isLinkNode(linkNode)) {
        setExistingLinkUrl(linkNode.getURL());
      } else {
        setExistingLinkUrl("");
      }

      const topElement =
        anchorNode.getKey() === "root" ? anchorNode : anchorNode.getTopLevelElementOrThrow();

      if ($isHeadingNode(topElement)) {
        setBlockType(topElement.getTag());
      } else if ($isQuoteNode(topElement)) {
        setBlockType("quote");
      } else {
        setBlockType("paragraph");
      }

      const parentList = $getNearestNodeOfType(anchorNode, ListNode);
      if (parentList) {
        const tag = parentList.getTag();
        setListType(tag === "ol" ? "number" : "bullet");
      } else {
        setListType(null);
      }
    }
  }, []);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => updateToolbar());
    });
  }, [editor, updateToolbar]);

  useEffect(() => {
    if (!showBlockDropdown) {
      return;
    }
    const handler = (e: MouseEvent) => {
      if (blockDropdownRef.current && !blockDropdownRef.current.contains(e.target as Node)) {
        setShowBlockDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler, true);
    return () => document.removeEventListener("mousedown", handler, true);
  }, [showBlockDropdown]);

  useEffect(() => {
    if (!showBlockDropdown) {
      return;
    }
    const rootElement = editor.getRootElement();
    if (!rootElement) {
      return;
    }
    const handler = () => setShowBlockDropdown(false);
    rootElement.addEventListener("mousedown", handler);
    return () => rootElement.removeEventListener("mousedown", handler);
  }, [showBlockDropdown, editor]);

  useEffect(() => {
    if (!showLinkPopover) {
      return;
    }
    const handler = (e: MouseEvent) => {
      if (linkPopoverRef.current && !linkPopoverRef.current.contains(e.target as Node)) {
        setShowLinkPopover(false);
      }
    };
    document.addEventListener("mousedown", handler, true);

    const rootElement = editor.getRootElement();
    rootElement?.addEventListener("mousedown", handler);

    return () => {
      document.removeEventListener("mousedown", handler, true);
      rootElement?.removeEventListener("mousedown", handler);
    };
  }, [showLinkPopover, editor]);

  useEffect(() => {
    if (showLinkPopover && linkInputRef.current) {
      linkInputRef.current.focus();
    }
  }, [showLinkPopover]);

  const setBlockFormat = (type: string) => {
    if (type === blockType) {
      setShowBlockDropdown(false);
      return;
    }
    editor.focus(() => {
      editor.update(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          return;
        }

        const createBlock: (() => ParagraphNode | QuoteNode | HeadingNode) | null =
          type === "paragraph"
            ? () => $createParagraphNode()
            : type === "quote"
            ? () => $createQuoteNode()
            : type.match(/^h[1-6]$/)
            ? () => $createHeadingNode(type as "h1" | "h2" | "h3" | "h4" | "h5" | "h6")
            : null;
        if (!createBlock) {
          return;
        }
        $setBlocksType(selection, createBlock);
      });
    });
    setShowBlockDropdown(false);
  };

  const dispatchTextFormat = (format: "bold" | "italic" | "strikethrough" | "code") => {
    editor.focus(() => {
      editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
    });
  };

  const toggleList = (type: "bullet" | "number") => {
    editor.focus(() => {
      if (listType === type) {
        editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
      } else if (type === "bullet") {
        editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
      } else {
        editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
      }
    });
  };

  const openLinkPopover = () => {
    if (isLink) {
      setLinkUrl(existingLinkUrl);
    } else {
      setLinkUrl("");
    }
    setShowLinkPopover(true);
  };

  const handleLinkSubmit = () => {
    const url = linkUrl.trim();
    if (url) {
      editor.focus(() => {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
      });
    }
    setShowLinkPopover(false);
    setLinkUrl("");
  };

  const handleLinkRemove = () => {
    editor.focus(() => {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    });
    setShowLinkPopover(false);
    setLinkUrl("");
  };

  return (
    <div className="px-3 py-1.5 flex items-center gap-0.5 border-b border-gray-100 shrink-0 flex-wrap">
      {/* Block type dropdown */}
      <div className="relative" ref={blockDropdownRef}>
        <button
          onMouseDown={e => {
            e.preventDefault();
            setShowBlockDropdown(v => !v);
          }}
          className="h-7 px-2 flex items-center gap-1 rounded hover:bg-gray-100 cursor-pointer transition-colors text-gray-700 text-xs font-medium min-w-[80px]"
        >
          {getBlockTypeLabel(blockType)}
          <span className="material-icons !text-[14px] text-gray-400">expand_more</span>
        </button>
        {showBlockDropdown && (
          <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-xl border border-gray-200 py-1 w-44 z-50">
            {blockTypeOptions.map(opt => (
              <button
                key={opt.value}
                onMouseDown={e => {
                  e.preventDefault();
                  setBlockFormat(opt.value);
                }}
                className={`w-full px-3 py-1.5 text-left cursor-pointer transition-colors ${
                  blockType === opt.value
                    ? "bg-blue-50 text-blue-700"
                    : "text-gray-700 hover:bg-gray-50"
                } ${BLOCK_TYPE_STYLES[opt.value] ?? "text-sm"}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="w-px h-5 bg-gray-200 mx-0.5" />

      <ToolbarButton label="Bold" onClick={() => dispatchTextFormat("bold")} active={isBold}>
        <span className="text-sm font-bold">B</span>
      </ToolbarButton>
      <ToolbarButton label="Italic" onClick={() => dispatchTextFormat("italic")} active={isItalic}>
        <span className="text-sm font-bold italic">I</span>
      </ToolbarButton>
      <ToolbarButton
        label="Strikethrough"
        onClick={() => dispatchTextFormat("strikethrough")}
        active={isStrikethrough}
      >
        <span className="text-sm line-through">S</span>
      </ToolbarButton>
      <ToolbarButton
        label="Code"
        onClick={() => dispatchTextFormat("code")}
        active={isCode}
        icon="code"
      />

      <div className="w-px h-5 bg-gray-200 mx-0.5" />

      <ToolbarButton
        icon="format_list_bulleted"
        label="Bullet List"
        onClick={() => toggleList("bullet")}
        active={listType === "bullet"}
      />
      <ToolbarButton
        icon="format_list_numbered"
        label="Numbered List"
        onClick={() => toggleList("number")}
        active={listType === "number"}
      />

      {showLinkButton && (
        <>
          <div className="w-px h-5 bg-gray-200 mx-0.5" />

          <div className="relative" ref={linkPopoverRef}>
            <ToolbarButton
              icon="link"
              label="Insert Link"
              onClick={openLinkPopover}
              active={isLink}
            />
            {showLinkPopover && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1.5 bg-white rounded-lg shadow-xl border border-gray-200 p-3 w-64 z-50">
                <label className="block text-xs font-medium text-gray-500 mb-1.5">URL</label>
                <input
                  ref={linkInputRef}
                  type="url"
                  value={linkUrl}
                  onChange={e => setLinkUrl(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleLinkSubmit();
                    }
                    if (e.key === "Escape") {
                      setShowLinkPopover(false);
                    }
                  }}
                  placeholder="https://..."
                  className="w-full border border-gray-200 rounded-md px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div className="flex items-center justify-end gap-2 mt-2.5">
                  {isLink && (
                    <button
                      onMouseDown={e => {
                        e.preventDefault();
                        handleLinkRemove();
                      }}
                      className="px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded cursor-pointer transition-colors"
                    >
                      Remove
                    </button>
                  )}
                  <button
                    onMouseDown={e => {
                      e.preventDefault();
                      handleLinkSubmit();
                    }}
                    disabled={!linkUrl.trim()}
                    className="px-2.5 py-1 text-xs font-medium bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer transition-colors"
                  >
                    {isLink ? "Update" : "Insert"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

const LinkPlugin: React.FC = () => {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    return editor.registerCommand(
      TOGGLE_LINK_COMMAND,
      (payload: string | null) => {
        $toggleLink(payload);
        return true;
      },
      1 // COMMAND_PRIORITY_EDITOR
    );
  }, [editor]);
  return null;
};

const GetMarkdownRefPlugin: React.FC<{
  getMarkdownRef: React.MutableRefObject<(() => string) | null>;
  preserveNewLines: boolean;
}> = ({ getMarkdownRef, preserveNewLines }) => {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    getMarkdownRef.current = () => {
      let markdown = "";
      editor.getEditorState().read(() => {
        markdown = $convertToMarkdownString(TRANSFORMERS, undefined, preserveNewLines);
      });
      return preserveNewLines ? encodeBlankLines(markdown) : markdown;
    };
    return () => {
      getMarkdownRef.current = null;
    };
  }, [editor, getMarkdownRef, preserveNewLines]);
  return null;
};

// Emits the current content as a markdown string on every edit (opt-in via the
// onMarkdownChange prop). Lets callers track dirtiness / length without a ref pull.
const MarkdownChangePlugin: React.FC<{
  onChange: (markdown: string) => void;
  preserveNewLines: boolean;
}> = ({ onChange, preserveNewLines }) => {
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const markdown = $convertToMarkdownString(TRANSFORMERS, undefined, preserveNewLines);
        onChange(preserveNewLines ? encodeBlankLines(markdown) : markdown);
      });
    });
  }, [editor, onChange, preserveNewLines]);
  return null;
};

export interface RichTextEditorProps {
  initialMarkdown: string;
  /** Required when editable; ref returns plain markdown string */
  getMarkdownRef?: React.MutableRefObject<(() => string) | null>;
  /** Optional: fires the current content as markdown on every edit. */
  onMarkdownChange?: (markdown: string) => void;
  placeholder?: string;
  minHeight?: string;
  showToolbar?: boolean;
  /** When false, renders read-only (e.g. for TextBlock display). Default true */
  editable?: boolean;
  className?: string;
  /** Optional namespace for Lexical to avoid conflicts when multiple instances exist in DOM */
  namespace?: string;
  /** Optional content class name (e.g. text-card-foreground for themed display) */
  contentClassName?: string;
  /**
   * When true, the toolbar stays pinned and only the content region scrolls
   * (the editor fills its parent's height). Give the parent a fixed height.
   * Default false — content grows to fit (e.g. inline TextBlock display).
   */
  scrollableContent?: boolean;
  /**
   * Deepest heading the toolbar dropdown offers. Default 6 (full H1–H6, e.g.
   * dashboard text blocks); the memory editor passes 4 per the 2026-07-30 design
   * review. Deeper headings in existing content still render via the theme.
   */
  maxHeadingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  /**
   * When true, blank lines the user types survive the markdown round trip, while
   * the persisted markdown keeps standard semantics ("\n\n" = block separator,
   * extra blank lines = real gaps — see markdownBlankLines.ts). This makes the
   * read-only view match what was typed AND renders agent-written / legacy notes
   * tight. Default false: dashboard text blocks keep the historical
   * collapse-blank-lines markdown format so existing saved blocks render unchanged.
   */
  preserveNewLines?: boolean;
  /**
   * When true, links render in the standard blue link treatment. Only for plain
   * gray/white surfaces (memory drawer); text blocks must keep inheriting their
   * themed card foreground or links can lose contrast on tinted cards.
   */
  accentLinks?: boolean;
  /**
   * When false, the toolbar omits the Insert Link button. The memory editor
   * turns it off: memories are consumed by agents as plain text and URLs are
   * never fetched, so a link there does nothing. Existing links in content
   * still render either way. Default true.
   */
  showLinkButton?: boolean;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  initialMarkdown,
  getMarkdownRef,
  onMarkdownChange,
  placeholder = "Start typing...",
  minHeight = "200px",
  showToolbar = true,
  editable = true,
  className = "",
  namespace = "RichTextEditor",
  contentClassName = "text-gray-900 text-base",
  scrollableContent = false,
  maxHeadingLevel = 6,
  preserveNewLines = false,
  accentLinks = false,
  showLinkButton = true,
}) => {
  const initialConfig = {
    namespace,
    nodes: [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode],
    onError: (error: Error) => console.error("Lexical error:", error),
    editable,
    editorState: () => {
      const markdown = preserveNewLines
        ? decodeBlankLines(initialMarkdown || "")
        : initialMarkdown || "";
      $convertFromMarkdownString(markdown, TRANSFORMERS, undefined, preserveNewLines);
    },
    theme: accentLinks ? LEXICAL_TEXT_THEME_ACCENT_LINKS : LEXICAL_TEXT_THEME,
  };

  const showToolbarAndPlugins = editable && showToolbar;

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className={`flex flex-col flex-1 ${scrollableContent ? "min-h-0" : ""} ${className}`}>
        {showToolbarAndPlugins && (
          <ToolbarPlugin maxHeadingLevel={maxHeadingLevel} showLinkButton={showLinkButton} />
        )}
        <div className={`relative flex-1 ${scrollableContent ? "overflow-y-auto min-h-0" : ""}`}>
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                className={`px-4 py-3 focus:outline-none text-left ${
                  editable ? "cursor-text" : "cursor-default"
                } ${contentClassName}`}
                style={editable ? { minHeight } : undefined}
              />
            }
            placeholder={
              editable ? (
                <div className="absolute top-3 left-4 text-gray-400 leading-relaxed text-base pointer-events-none">
                  {placeholder}
                </div>
              ) : null
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <LinkPlugin />
          {editable && (
            <>
              <HistoryPlugin />
              <ListPlugin />
              <AutoFocusPlugin />
              {getMarkdownRef && (
                <GetMarkdownRefPlugin
                  getMarkdownRef={getMarkdownRef}
                  preserveNewLines={preserveNewLines}
                />
              )}
              {onMarkdownChange && (
                <MarkdownChangePlugin
                  onChange={onMarkdownChange}
                  preserveNewLines={preserveNewLines}
                />
              )}
            </>
          )}
        </div>
      </div>
    </LexicalComposer>
  );
};
